import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import {
  ArrowRight, TrendingUp, TrendingDown, Minus, Download, RefreshCw,
  AlertTriangle, FileText, Plus, X,
} from 'lucide-react';
import { toast } from 'sonner';
import api from '../../lib/axios';
import DateInput from '@/components/ui/date-input';
import { useClientListMode } from '@/hooks/useClientListMode';
import ListModePager from '@/components/ListModePager';
import { statusLabel } from '@/lib/deLabels';

const fmtMoney = (n) =>
  new Intl.NumberFormat('de-DE', { style: 'currency', currency: 'EUR' }).format(n || 0);

const STATUS_META = {
  increased: { icon: TrendingUp, label: 'Erhöht', cls: 'bg-amber-100 text-amber-800 border-amber-200' },
  decreased: { icon: TrendingDown, label: 'Reduziert', cls: 'bg-rose-100 text-rose-800 border-rose-200' },
  unchanged: { icon: Minus, label: 'Unverändert', cls: 'bg-slate-100 text-slate-700 border-slate-200' },
  added: { icon: Plus, label: 'Neu hinzugefügt', cls: 'bg-emerald-100 text-emerald-800 border-emerald-200' },
  removed: { icon: X, label: 'Entfernt', cls: 'bg-slate-200 text-slate-700 border-slate-300' },
};

/**
 * Embedded panel showing v1 (cancelled) ↔ v2 (open) invoice comparison
 * for a given tenant + billing month. Used inside Tabs in BillingPreviewPage.
 */
export default function BillingV1V2ComparePanel({ tenantId, defaultPeriod = '2026-04-01' }) {
  const [period, setPeriod] = useState(defaultPeriod);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [filter, setFilter] = useState('all');

  const load = useCallback(async () => {
    if (!tenantId) {
      setData(null);
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const res = await api.get('/billing/v1-v2-compare', {
        params: { tenant_id: tenantId, billing_month: period },
      });
      setData(res.data);
    } catch (err) {
      const msg = err?.response?.data?.detail || 'Vergleich konnte nicht geladen werden.';
      setError(typeof msg === 'string' ? msg : 'Fehler beim Laden.');
      toast.error(typeof msg === 'string' ? msg : 'Fehler beim Laden.');
    } finally {
      setLoading(false);
    }
  }, [tenantId, period]);

  useEffect(() => {
    load();
  }, [load]);

  const downloadCsv = async () => {
    try {
      const res = await api.get('/billing/v1-v2-compare/csv', {
        params: { tenant_id: tenantId, billing_month: period },
        responseType: 'blob',
      });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement('a');
      a.href = url;
      a.download = `v1-v2-compare_${data?.tenant_slug || 'tenant'}_${period}.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success('CSV heruntergeladen');
    } catch (err) {
      toast.error('CSV-Export fehlgeschlagen');
    }
  };

  const openInvoicePdf = async (invoiceId, invoiceNumber) => {
    if (!invoiceId) return;
    try {
      const res = await api.get(`/invoices/${invoiceId}/pdf`, { responseType: 'blob' });
      const blobUrl = window.URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
      window.open(blobUrl, '_blank');
      // Revoke after a short delay so the new tab can load it
      setTimeout(() => window.URL.revokeObjectURL(blobUrl), 30000);
    } catch (err) {
      toast.error(`PDF für ${invoiceNumber} konnte nicht geladen werden`);
    }
  };

  const filteredRows = data?.rows?.filter((r) => filter === 'all' || r.status === filter) || [];
  const {
    listMode, setListMode, pageSize, setPageSize, listPage, setListPage,
    totalCount, pageCount, visibleItems: visibleRows,
  } = useClientListMode(filteredRows, {
    resetKey: `${filter}|${data?.rows?.length || 0}`,
  });

  return (
    <div className="space-y-4" data-testid="v1-v2-compare-panel">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <p className="text-sm text-slate-600">
          Stornierte (v1) vs. neu generierte (v2) Rechnungen pro Familie · für Audit + Buchhaltung.
        </p>
        <div className="flex items-center gap-2 flex-wrap">
          <DateInput  value={period} onChange={(e) => setPeriod(e.target.value)}
            className="px-3 py-2 text-sm border border-slate-300 rounded-md bg-white"
            data-testid="compare-period-input" />
          <Button onClick={load} variant="outline" size="sm" disabled={loading} data-testid="compare-reload-btn">
            <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
            Laden
          </Button>
          <Button onClick={downloadCsv} size="sm" disabled={!data || loading} data-testid="compare-csv-btn">
            <Download className="w-4 h-4 mr-2" />
            CSV
          </Button>
        </div>
      </div>

      {error && (
        <Card className="border-rose-200 bg-rose-50">
          <CardContent className="p-4 text-sm text-rose-800 flex items-start gap-2">
            <AlertTriangle className="w-4 h-4 mt-0.5" /> {error}
          </CardContent>
        </Card>
      )}

      {!error && !loading && data && data.v1_invoice_count === 0 && data.v2_invoice_count === 0 && (
        <Card className="border-slate-200 bg-slate-50">
          <CardContent className="p-6 text-center text-sm text-slate-600">
            Für diesen Mandanten + Monat liegen weder stornierte noch offene Rechnungen vor.
          </CardContent>
        </Card>
      )}

      {data && (data.v1_invoice_count > 0 || data.v2_invoice_count > 0) && (
        <>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <Card data-testid="summary-v1">
              <CardContent className="p-4">
                <p className="text-xs uppercase tracking-wide text-slate-500 font-semibold">v1 (storniert)</p>
                <p className="text-2xl font-bold text-slate-700 mt-2">{fmtMoney(data.v1_total)}</p>
                <p className="text-xs text-slate-500 mt-1">{data.v1_invoice_count} Rechnungen</p>
              </CardContent>
            </Card>
            <Card data-testid="summary-v2">
              <CardContent className="p-4">
                <p className="text-xs uppercase tracking-wide text-slate-500 font-semibold">v2 (neu)</p>
                <p className="text-2xl font-bold text-slate-900 mt-2">{fmtMoney(data.v2_total)}</p>
                <p className="text-xs text-slate-500 mt-1">{data.v2_invoice_count} Rechnungen</p>
              </CardContent>
            </Card>
            <Card className={data.delta_total >= 0 ? 'border-emerald-200' : 'border-rose-200'}
              data-testid="summary-delta">
              <CardContent className="p-4">
                <p className="text-xs uppercase tracking-wide text-slate-500 font-semibold">Δ Mehrumsatz</p>
                <p className={`text-2xl font-bold mt-2 ${data.delta_total >= 0 ? 'text-emerald-700' : 'text-rose-700'}`}>
                  {data.delta_total >= 0 ? '+' : ''}{fmtMoney(data.delta_total)}
                </p>
                <p className="text-xs text-slate-500 mt-1">v2 − v1</p>
              </CardContent>
            </Card>
            <Card data-testid="summary-stats">
              <CardContent className="p-4">
                <p className="text-xs uppercase tracking-wide text-slate-500 font-semibold">Bewegung</p>
                <div className="mt-2 grid grid-cols-2 gap-1 text-xs">
                  <span className="text-amber-700">↑ {data.stats.increased}</span>
                  <span className="text-rose-700">↓ {data.stats.decreased}</span>
                  <span className="text-slate-500">= {data.stats.unchanged}</span>
                  <span className="text-emerald-700">+ {data.stats.added}</span>
                </div>
              </CardContent>
            </Card>
          </div>

          <div className="flex items-center gap-2 flex-wrap text-sm">
            <span className="text-slate-500">Filter:</span>
            {['all', 'increased', 'decreased', 'unchanged', 'added', 'removed'].map((s) => {
              const meta = STATUS_META[s];
              const count = s === 'all' ? data.rows.length : (data.stats[s] ?? 0);
              if (s !== 'all' && count === 0) return null;
              return (
                <button key={s}
                  onClick={() => setFilter(s)}
                  className={`px-3 py-1 rounded-full border text-xs font-medium transition ${
                    filter === s
                      ? 'bg-brand-red text-white border-brand-red'
                      : 'bg-white border-slate-300 hover:bg-slate-50'
                  }`}
                  data-testid={`compare-filter-${s}`}>
                  {s === 'all' ? 'Alle' : (meta?.label || s)} ({count})
                </button>
              );
            })}
          </div>

          <Card>
            <CardContent className="p-0 overflow-x-auto">
              <ListModePager
                listMode={listMode}
                setListMode={setListMode}
                pageSize={pageSize}
                setPageSize={setPageSize}
                listPage={listPage}
                setListPage={setListPage}
                totalCount={totalCount}
                pageCount={pageCount}
                visibleCount={visibleRows.length}
                label="Vergleiche"
                testId="billing-compare-list-pager"
              />
              <table className="w-full text-sm" data-testid="compare-table">
                <thead className="bg-slate-50 border-b border-slate-200">
                  <tr className="text-left text-slate-600">
                    <th className="px-4 py-3 font-semibold">Familie / Zahler</th>
                    <th className="px-4 py-3 font-semibold">v1-Rechnungsnr.</th>
                    <th className="px-4 py-3 font-semibold text-right">v1 €</th>
                    <th className="px-4 py-3"></th>
                    <th className="px-4 py-3 font-semibold">v2-Rechnungsnr.</th>
                    <th className="px-4 py-3 font-semibold text-right">v2 €</th>
                    <th className="px-4 py-3 font-semibold text-right">Δ €</th>
                    <th className="px-4 py-3 font-semibold">Status</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredRows.length === 0 && (
                    <tr>
                      <td colSpan={8} className="px-4 py-8 text-center text-slate-500">
                        Keine Einträge im aktuellen Filter
                      </td>
                    </tr>
                  )}
                  {visibleRows.map((r) => {
                    const meta = STATUS_META[r.status];
                    const Icon = meta?.icon || FileText;
                    return (
                      <tr key={r.family_key}
                        className="border-b border-slate-100 hover:bg-slate-50"
                        data-testid={`compare-row-${r.family_key}`}>
                        <td className="px-4 py-3 font-medium">{r.label}</td>
                        <td className="px-4 py-3 font-mono text-xs text-slate-500">
                          {r.v1_invoice_number ? (
                            <button
                              onClick={() => openInvoicePdf(r.v1_invoice_id, r.v1_invoice_number)}
                              className="text-brand-red hover:text-brand-red hover:underline"
                              title="PDF in neuem Tab öffnen"
                              data-testid={`v1-pdf-${r.family_key}`}
                            >
                              {r.v1_invoice_number}
                            </button>
                          ) : '—'}
                        </td>
                        <td className="px-4 py-3 text-right tabular-nums text-slate-500">
                          {r.v1_total ? fmtMoney(r.v1_total) : '—'}
                        </td>
                        <td className="px-4 py-3 text-slate-300">
                          <ArrowRight className="w-4 h-4" />
                        </td>
                        <td className="px-4 py-3 font-mono text-xs">
                          {r.v2_invoice_number ? (
                            <button
                              onClick={() => openInvoicePdf(r.v2_invoice_id, r.v2_invoice_number)}
                              className="text-brand-red font-semibold hover:text-brand-red hover:underline"
                              title="PDF in neuem Tab öffnen"
                              data-testid={`v2-pdf-${r.family_key}`}
                            >
                              {r.v2_invoice_number}
                            </button>
                          ) : '—'}
                        </td>
                        <td className="px-4 py-3 text-right tabular-nums font-medium">
                          {r.v2_total ? fmtMoney(r.v2_total) : '—'}
                        </td>
                        <td className={`px-4 py-3 text-right tabular-nums font-semibold ${
                          r.delta > 0 ? 'text-amber-700' : r.delta < 0 ? 'text-rose-700' : 'text-slate-400'
                        }`}>
                          {r.delta === 0 ? '—' : (r.delta > 0 ? '+' : '') + fmtMoney(r.delta)}
                        </td>
                        <td className="px-4 py-3">
                          <Badge className={`${meta?.cls || ''} text-xs font-normal`}>
                            <Icon className="w-3 h-3 mr-1 inline" /> {meta?.label || statusLabel(r.status)}
                          </Badge>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </CardContent>
          </Card>
        </>
      )}
    </div>
  );
}
